"use client";

import { useState } from "react";

import { isUncommonJapaneseName, JAPAN_NATIONALITY } from "@/types/validation";

type NationalityFormValues = {
  nationality: string;
  lastName?: string;
  firstName?: string;
};

/**
 * 氏名が日本の一般的な表記と異なるのに国籍が「日本」のままのとき、保存前に確認を挟む。
 * 確認で「日本のまま保存する」を選べばそのまま保存できる（保存自体は拒否しない）。
 */
export function useNationalityConfirm<
  FORM_VALUES extends NationalityFormValues,
>(save: (values: FORM_VALUES) => Promise<void> | void) {
  const [pendingValues, setPendingValues] = useState<FORM_VALUES | null>(null);

  const submit = async (values: FORM_VALUES) => {
    const needsConfirm =
      values.nationality === JAPAN_NATIONALITY &&
      isUncommonJapaneseName({
        lastName: values.lastName,
        firstName: values.firstName,
      });

    if (needsConfirm) {
      setPendingValues(values);
      return;
    }

    await save(values);
  };

  const confirm = async () => {
    const values = pendingValues;
    setPendingValues(null);
    if (values) await save(values);
  };

  return {
    /** フォームの onSubmit に渡す。確認が必要なときは保存せずダイアログを開く */
    submit,
    /** 確認ダイアログを開くかどうか */
    opened: pendingValues !== null,
    /** 「日本のまま保存する」を選んだとき */
    confirm,
    /** 「国籍を入力し直す」を選んだとき */
    cancel: () => setPendingValues(null),
  };
}
